import axios from "axios";

// Shared axios instance pointing at the backend API
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
});

// Attach the JWT (if present) to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

// Broadcast that the Zerodha broker session has expired so the UI can prompt
// a reconnect. Deliberately a window event (not a logout): the user's own JWT
// is still valid — only the daily broker token died. Transport-agnostic, so
// fetch-based callers can fire it too.
export const notifyBrokerSessionExpired = () => {
  window.dispatchEvent(new CustomEvent("broker-session-expired"));
};

// A 401 from a /kite/* endpoint means the broker token expired (the backend
// returns 401 for that case). Surface the reconnect prompt; never log out.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error.config && error.config.url ? error.config.url : "";
    if (error.response && error.response.status === 401 && url.includes("/kite/")) {
      notifyBrokerSessionExpired();
    }
    return Promise.reject(error);
  }
);

// ── Strategies ──────────────────────────────────────────────
export const strategyAPI = {
  getAll: () => api.get("/strategies"),
  getDeployed: () => api.get("/strategies/deployed"),
  // Full day's engine activity for every deployed strategy (orders, fills,
  // exits + reason, broker rejections, blocked entries). date = IST YYYY-MM-DD.
  activity: (date) => api.get("/strategies/activity", { params: date ? { date } : {} }),
  create: (data) => api.post("/strategies", data),
  update: (id, data) => api.put(`/strategies/${id}`, data),
  remove: (id) => api.delete(`/strategies/${id}`),
  deploy: (id, mode, brokerId) => api.post(`/strategies/${id}/deploy`, { ...(mode ? { mode } : {}), ...(brokerId ? { brokerId } : {}) }),
  // Approve an AI-generated strategy for live deploy (after backtest review).
  approve: (id) => api.post(`/strategies/${id}/approve`),
  // Custom-formula generator: text idea → reviewable strategy.
  fromText: (text) => api.post("/strategies/from-text", { text }),
  // Live validation for the formula operand editor.
  validateFormula: (expr) => api.post("/strategies/validate-formula", { expr }),
};

// ── Backtesting ─────────────────────────────────────────────
export const backtestAPI = {
  getCredits: () => api.get("/backtest/credits"),
  run: (payload) => api.post("/backtest/run", payload),
  getResult: (id) => api.get(`/backtest/results/${id}`),
  // Read-only chart overlay: real ₹ P&L + per-trade geometry, no credit charge.
  overlay: (strategyId, from, to) => api.post("/backtest/overlay", { strategyId, from, to }),
};

// ── Simulated Trading (strategy replay on historical candles) ──
export const simulationAPI = {
  prepare: (payload) => api.post("/simulation/prepare", payload),
};

// ── Question Answer (natural-language Q&A over downloaded historical data) ──
export const qaAPI = {
  ask: (question, history) => api.post("/qa/ask", { question, history }),
};

// ── Historical data store (backtest candles cached locally) ──
export const historicalDataAPI = {
  summary: () => api.get("/historical-data/summary"),
  status: () => api.get("/historical-data/status"),
  save: (payload) => api.post("/historical-data/save", payload),
  cancel: () => api.post("/historical-data/cancel"),
  remove: (underlying) => api.delete(`/historical-data/${underlying}`),
  // Pre-download report: what the .xlsx export for this range would contain
  // (sheets, expiries, rows, approx size) — review before downloading.
  report: (underlying, from, to) => api.get(`/historical-data/report/${underlying}`, { params: { from, to } }),
  // Direct browser download URL (token in query so the file streams to disk).
  // Pass from/to to limit to a range; omit for everything saved.
  exportUrl: (underlying, from, to) => {
    const base = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");
    const params = new URLSearchParams({ token: localStorage.getItem("token") || "" });
    if (from && to) { params.set("from", from); params.set("to", to); }
    return `${base}/historical-data/export/${underlying}?${params.toString()}`;
  },
};

// Expired-option candle chart: pass a trading symbol (e.g. NIFTY2661623200CE) and a
// timeframe (1minute | 5minute | 15minute) → { meta, candles }.
export const optionChartAPI = {
  get: (symbol, interval) =>
    api.get(`/option-chart/${encodeURIComponent(symbol)}`, { params: { interval } }),
  expiries: (underlying) => api.get(`/option-chart/meta/expiries/${underlying}`),
  strikes: (underlying, expiry) => api.get(`/option-chart/meta/strikes/${underlying}/${expiry}`),
  search: (q, underlying) => api.get(`/option-chart/search`, { params: { q, underlying } }),
  index: (underlying, interval, from, to) => api.get(`/option-chart/index/${underlying}`, { params: { interval, from, to } }),
  // Crypto (Delta) perp candles for a date range — TFs up to 4-hour, IST-labelled.
  cryptoIndex: (underlying, interval, from, to) => api.get(`/option-chart/crypto/index/${underlying}`, { params: { interval, from, to } }),
  // Crypto per-strike option chart: expiries → strikes → one contract's candles.
  cryptoExpiries: (underlying) => api.get(`/option-chart/crypto/expiries/${underlying}`),
  cryptoStrikes: (underlying, expiry) => api.get(`/option-chart/crypto/strikes/${underlying}`, { params: { expiry } }),
  cryptoOption: (underlying, opts) => api.get(`/option-chart/crypto/option/${underlying}`, { params: opts }),
};

// Data Analysis — day-wise underlying stats + ATM implied-vol (VIX-like) series.
export const dataAnalysisAPI = {
  daily: (underlying, from, to) => api.get(`/data-analysis/daily/${underlying}`, { params: { from, to } }),
  iv: (underlying, from, to) => api.get(`/data-analysis/iv/${underlying}`, { params: { from, to } }),
  // Crypto VIX candlestick — intraday ATM-IV bucketed to OHLC at `interval`.
  ivCandles: (underlying, opts) => api.get(`/data-analysis/iv-candles/${underlying}`, { params: opts }),
};

// Reports / analytics
export const reportsAPI = {
  get: (from, to, mode) => api.get(`/reports`, { params: { from, to, mode } }),
};

// Pre-market brief: daily 08:30 IST analysis of free overnight cues → opening
// bias + day-regime probabilities. `run` regenerates today's on demand.
export const preMarketAPI = {
  today: () => api.get(`/premarket/today`),
  history: (limit) => api.get(`/premarket/history`, { params: limit ? { limit } : {} }),
  run: () => api.post(`/premarket/run`),
};

// Strategy Terminal: conversational create/modify for the CALLER'S OWN
// strategies only (routes/strategyChat.js) — same session/run/poll-job shape as
// adminAPI's aiCmd* below, different (and much narrower) backend agent.
export const strategyChatAPI = {
  sessions: () => api.get(`/strategy-chat/sessions`),
  session: (id) => api.get(`/strategy-chat/sessions/${id}`),
  newSession: () => api.post(`/strategy-chat/sessions`),
  run: (id, prompt) => api.post(`/strategy-chat/sessions/${id}/run`, { prompt }),
  job: (id) => api.get(`/strategy-chat/jobs/${id}`),
};

// Current logged-in user (includes isAdmin) — used to gate the Admin panel/nav.
export const authAPI = {
  me: () => api.get(`/auth/me`),
  // WhatsApp-alert opt-in: { whatsappAlerts, whatsappNumber }.
  updateNotifications: (data) => api.put(`/auth/notifications`, data),
};

// Brokers — incl. the Delta Exchange (crypto) connect (save key + validate).
export const brokerAPI = {
  list: () => api.get(`/brokers`),
  deltaConnect: (data) => api.post(`/brokers/delta/connect`, data),
};

// Admin-only: manage all users + control feature-flag rollout. Every call 403s
// for non-admin tokens (server-side guard), so the UI also hides itself.
export const adminAPI = {
  listUsers: () => api.get(`/admin/users`),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, body) => api.patch(`/admin/users/${id}`, body),
  deleteUser: (id, force) => api.delete(`/admin/users/${id}${force ? '?force=true' : ''}`),
  // Mint a session token for `id` so the admin can enter the portal as that user.
  loginAs: (id) => api.post(`/admin/users/${id}/login`),
  listFlags: () => api.get(`/admin/flags`),
  createFlag: (body) => api.post(`/admin/flags`, body),
  applyAll: (key) => api.post(`/admin/flags/${encodeURIComponent(key)}/apply-all`),
  rollback: (key) => api.post(`/admin/flags/${encodeURIComponent(key)}/rollback`),

  // ── YouTube Brain: distil option-trading videos → strategy knowledge that
  // grounds the "Create a Strategy with a Prompt" builder (RAG). ──
  ytList: (category, status) => api.get(`/admin/youtube`, { params: { ...(category ? { category } : {}), ...(status ? { status } : {}) } }),
  ytIngestChannel: (body) => api.post(`/admin/youtube/ingest-channel`, body),
  ytDiscover: (body) => api.post(`/admin/youtube/discover`, body),
  ytDiscoverDefaults: () => api.get(`/admin/youtube/discover/defaults`),
  ytGetDiscoveryConfig: () => api.get(`/admin/youtube/discovery-config`),
  ytSaveDiscoveryConfig: (body) => api.put(`/admin/youtube/discovery-config`, body),
  ytJob: (id) => api.get(`/admin/youtube/jobs/${id}`),
  ytIngestVideo: (body) => api.post(`/admin/youtube/ingest-video`, body),
  ytTranscript: (id, transcript) => api.post(`/admin/youtube/${id}/transcript`, { transcript }),
  ytUpdate: (id, body) => api.patch(`/admin/youtube/${id}`, body),
  ytDelete: (id) => api.delete(`/admin/youtube/${id}`),
  ytCookiesStatus: () => api.get(`/admin/youtube/cookies/status`),
  ytSaveCookies: (cookies, clear) => api.post(`/admin/youtube/cookies`, { cookies, clear }),

  // ── Dynamic Strategy Builder: knowledge → variants → bulk backtest → leaderboard ──
  dynRun: (body) => api.post(`/admin/dynamic/run`, body),
  dynJob: (id) => api.get(`/admin/dynamic/jobs/${id}`),
  dynLeaderboard: (category, sortBy, limit) => api.get(`/admin/dynamic/leaderboard`, { params: { ...(category ? { category } : {}), ...(sortBy ? { sortBy } : {}), ...(limit ? { limit } : {}) } }),
  dynGet: (id) => api.get(`/admin/dynamic/${id}`),
  dynSaveStrategy: (id) => api.post(`/admin/dynamic/${id}/save-strategy`),
  dynDelete: (id) => api.delete(`/admin/dynamic/${id}`),

  // ── AI Command: Claude agent with direct file/shell access to the server.
  // No approval step — every action lands live. Full transcript (incl. every
  // tool call) is the audit log, fetched per-session. ──
  aiCmdSessions: () => api.get(`/admin/ai-command/sessions`),
  aiCmdSession: (id) => api.get(`/admin/ai-command/sessions/${id}`),
  aiCmdNewSession: () => api.post(`/admin/ai-command/sessions`),
  aiCmdRun: (id, prompt) => api.post(`/admin/ai-command/sessions/${id}/run`, { prompt }),
  aiCmdJob: (id) => api.get(`/admin/ai-command/jobs/${id}`),

  // ── Terminal: real shell (WebSocket, see terminalWsUrl below) — this is just
  // the read-only audit list of past sessions. ──
  termSessions: () => api.get(`/admin/terminal/sessions`),
  termSession: (id) => api.get(`/admin/terminal/sessions/${id}`),
};

// wss://<host>/api/admin/terminal/ws (or ws:// on http) — derived from
// REACT_APP_API_URL the same way the rest of api.js resolves the backend,
// but WebSocket can't go through the axios instance.
export const terminalWsUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const path = base.startsWith("http") ? base.replace(/^https?:/, proto) : `${proto}//${window.location.host}${base}`;
  return `${path}/admin/terminal/ws`;
};

export default api;
